import {
  Alert,
  Button,
  CloseButton,
  Heading,
  Modal,
  NumberInput,
  SimpleSelect,
  Spinner,
  Text,
} from "@instructure/ui";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfigService } from "../../services/ConfigService";
import { MasteryLevelContext } from "../../utils/Context";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";

const FinalizationSettingsModal = ({
  open,
  onClose,
  deadline,
  existingScale,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { masteryLevels } = useContext(MasteryLevelContext);
  const levels = masteryLevels || [];

  const [deadlineDays, setDeadlineDays] = useState("");
  const [masteredLevelId, setMasteredLevelId] = useState("");
  const [notMasteredLevelId, setNotMasteredLevelId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (deadline >= 0) {
      setDeadlineDays(deadline);
    }
  }, [deadline]);

  useEffect(() => {
    if (existingScale && existingScale.levels) {
      const mastered = existingScale.levels.find(
        (l) => String(l.points) === "1"
      );
      const notMastered = existingScale.levels.find(
        (l) => String(l.points) === "0"
      );
      if (mastered) setMasteredLevelId(mastered.mastery_level_id);
      if (notMastered) setNotMasteredLevelId(notMastered.mastery_level_id);
    }
  }, [existingScale]);

  const handleDismiss = () => {
    onClose(false);
  };

  const handleExited = () => {
    // Reset state after modal exit animation completes
    setDeadlineDays(deadline >= 0 ? deadline : "");
    setValidationError("");
    if (existingScale && existingScale.levels) {
      const mastered = existingScale.levels.find(
        (l) => String(l.points) === "1"
      );
      const notMastered = existingScale.levels.find(
        (l) => String(l.points) === "0"
      );
      setMasteredLevelId(mastered ? mastered.mastery_level_id : "");
      setNotMasteredLevelId(notMastered ? notMastered.mastery_level_id : "");
    } else {
      setMasteredLevelId("");
      setNotMasteredLevelId("");
    }
  };

  const handleDeadlineChange = (_e, value) => {
    let inputValue = value.replace(/\D/g, "");
    if (inputValue) {
      inputValue = Math.min(Number(inputValue), 30);
    }
    setDeadlineDays(inputValue === "" ? "" : inputValue);
  };

  const handleIncrementDeadline = () => {
    setDeadlineDays((prev) => (prev === 30 ? prev : Number(prev || 0) + 1));
  };

  const handleDecrementDeadline = () => {
    setDeadlineDays((prev) => (prev > 0 ? Number(prev) - 1 : prev));
  };

  const handleSubmit = async () => {
    if (masteredLevelId && notMasteredLevelId) {
      if (masteredLevelId === notMasteredLevelId) {
        setValidationError(t("finalizationSettings.levelsMustBeDifferent"));
        return;
      }
    }
    setValidationError("");

    try {
      setSubmitting(true);

      // Save deadline
      const deadlinePayload = deadlineDays === "" ? -1 : deadlineDays;
      await ConfigService.setGlobalDaysAfterTermDeadline(deadlinePayload);

      // Save binary baseline scale (only if both levels selected)
      if (masteredLevelId && notMasteredLevelId) {
        await ConfigService.createOrUpdateBinaryBaselineScale(
          masteredLevelId,
          notMasteredLevelId
        );
      }

      showNotification(
        t("notifications.general.updateSuccess", {
          obj: t("finalizationSettings.title"),
        })
      );
      onClose(true);
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("finalizationSettings.title"),
        }),
        ERROR
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onDismiss={handleDismiss}
      onExited={handleExited}
      size="medium"
      label={t("finalizationSettings.modalLabel")}
      shouldCloseOnDocumentClick
    >
      <Modal.Header>
        <CloseButton
          placement="end"
          offset="small"
          onClick={handleDismiss}
          screenReaderLabel={t("global.close")}
        />
        <Heading as="h3">{t("finalizationSettings.title")}</Heading>
      </Modal.Header>
      <Modal.Body overflow="scroll">
        {validationError && (
          <Alert variant="error" margin="0 0 small 0">
            {validationError}
          </Alert>
        )}

        <Heading as="h4" margin="0 0 small 0">
          {t("finalizationSettings.deadlineHeading")}
        </Heading>
        <NumberInput
          renderLabel={t("resultsDeadline.daysAfterTerm")}
          value={deadlineDays}
          onChange={handleDeadlineChange}
          onIncrement={handleIncrementDeadline}
          onDecrement={handleDecrementDeadline}
          min={0}
          max={30}
        />

        <Heading as="h4" margin="medium 0 small 0">
          {t("finalizationSettings.binaryScaleHeading")}
        </Heading>
        <Text as="p" size="small" color="secondary">
          {t("finalizationSettings.binaryScaleDescription")}
        </Text>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            marginTop: "0.75rem",
          }}
        >
          <SimpleSelect
            renderLabel={t("finalizationSettings.masteredLabel")}
            value={masteredLevelId}
            onChange={(_e, { value }) => {
              setMasteredLevelId(value);
              setValidationError("");
            }}
            visibleOptionsCount={5}
          >
            {!existingScale && (
              <SimpleSelect.Option id="mastered-placeholder" value="">
                {t("finalizationSettings.selectPlaceholder")}
              </SimpleSelect.Option>
            )}
            {levels.map((level) => (
              <SimpleSelect.Option
                key={level.id}
                id={`mastered-${level.id}`}
                value={level.id}
              >
                {level.level}
              </SimpleSelect.Option>
            ))}
          </SimpleSelect>
          <SimpleSelect
            renderLabel={t("finalizationSettings.notMasteredLabel")}
            value={notMasteredLevelId}
            onChange={(_e, { value }) => {
              setNotMasteredLevelId(value);
              setValidationError("");
            }}
            visibleOptionsCount={5}
          >
            {!existingScale && (
              <SimpleSelect.Option id="not-mastered-placeholder" value="">
                {t("finalizationSettings.selectPlaceholder")}
              </SimpleSelect.Option>
            )}
            {levels.map((level) => (
              <SimpleSelect.Option
                key={level.id}
                id={`not-mastered-${level.id}`}
                value={level.id}
              >
                {level.level}
              </SimpleSelect.Option>
            ))}
          </SimpleSelect>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleDismiss} margin="0 x-small 0 0">
          {t("global.cancel")}
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={submitting}
          renderIcon={
            submitting ? (
              <Spinner size="x-small" renderTitle={t("global.pleaseWait")} />
            ) : undefined
          }
        >
          {t("global.save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FinalizationSettingsModal;
